import React, { useEffect, useState } from 'react';
import { useDataProvider, useRecordContext } from 'react-admin';
import { useFeatureGateContext } from 'providers/featureGateContext';

interface AssignmentRecord {
  id?: number | string;
  root_assignment_id?: number | string;
  created_at?: string;
}

interface DisableMarkupsFeatureGateContextProps {
  overrideAssignmentID?: number | string;
}

const DisableMarkupsFeatureGateContext = ({
  overrideAssignmentID,
}: DisableMarkupsFeatureGateContextProps): React.ReactElement | null => {
  const record = useRecordContext<AssignmentRecord>();
  const dataProvider = useDataProvider();
  const { setCustomAttributes, setCustomIDs, statsigUser } = useFeatureGateContext();
  const [statsigRootAssignmentID, setStatsigRootAssignmentID] = useState<
    number | string | undefined
  >(undefined);
  const [createdAt, setCreatedAt] = useState<string | undefined>(undefined);
  const recordRootAssignmentID = record?.root_assignment_id;

  useEffect(() => {
    let mounted = true;

    // Our hierarchial system will be as follows to send created_at:
    // 1. look at the override assignment id provided by the user -> find root assignment created_at
    // 2. look at the record's current root_assignment created_at
    // 3. look at the current record's created_at
    (async () => {
      try {
        if (overrideAssignmentID && overrideAssignmentID !== recordRootAssignmentID) {
          const { data: overRideAssignment } = await dataProvider.getOne('Assignment', {
            id: overrideAssignmentID,
          });

          if (!mounted) {
            return;
          }

          const rootID = overRideAssignment.root_assignment_id;

          if (rootID) {
            const { data: rootAssignment } = await dataProvider.getOne('Assignment', {
              id: rootID,
            });

            if (!mounted) {
              return;
            }

            setStatsigRootAssignmentID(rootID);
            setCreatedAt(rootAssignment.created_at);
          } else {
            setStatsigRootAssignmentID(overrideAssignmentID);
            setCreatedAt(overRideAssignment.created_at);
          }
          return;
        }

        if (recordRootAssignmentID) {
          const { data: recordRoot } = await dataProvider.getOne('Assignment', {
            id: recordRootAssignmentID,
          });

          if (!mounted) {
            return;
          }

          setStatsigRootAssignmentID(recordRootAssignmentID);
          setCreatedAt(recordRoot?.created_at);
          return;
        }

        setStatsigRootAssignmentID(record?.id);
        setCreatedAt(record?.created_at);
      } catch (error) {
        if (!mounted) {
          return;
        }
        setStatsigRootAssignmentID(undefined);
        setCreatedAt(undefined);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [overrideAssignmentID, dataProvider, recordRootAssignmentID, record?.id, record?.created_at]);

  const currentRootAssignmentID = statsigUser?.customIDs?.root_assignment_id;
  const currentCreatedAt = statsigUser?.custom?.createdAt;
  const nextRootAssignmentID = statsigRootAssignmentID
    ? String(statsigRootAssignmentID)
    : undefined;
  const formattedAssignmentCurrentID = record?.id ? String(record.id) : undefined;

  useEffect(() => {
    if (!nextRootAssignmentID || !createdAt) {
      return;
    }

    if (currentRootAssignmentID === nextRootAssignmentID && currentCreatedAt === createdAt) {
      return;
    }

    if (setCustomIDs) {
      setCustomIDs({
        root_assignment_id: nextRootAssignmentID,
        current_assignment_id: formattedAssignmentCurrentID,
      });
    }

    if (setCustomAttributes && createdAt) {
      setCustomAttributes({ createdAt });
    }
  }, [
    nextRootAssignmentID,
    createdAt,
    currentRootAssignmentID,
    currentCreatedAt,
    formattedAssignmentCurrentID,
    setCustomIDs,
    setCustomAttributes,
  ]);

  return null;
};

export default DisableMarkupsFeatureGateContext;
